import type { User } from "firebase/auth"
import { env } from "@/config/env"
import { getAppleToken } from "@/shared/lib/apple-identity"

/**
 * Chat/socket identity is always the Firebase uid, regardless of provider.
 * Apple sign-in additionally carries `appleToken` as a separate field (see
 * `buildSocketAuthToken`); it is no longer used as the primary id.
 */
export function getChatUserId(user: User): string {
  return user.uid
}

function base64UrlFromBytes(bytes: Uint8Array): string {
  let binary = ""
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}

function base64UrlFromString(value: string): string {
  return base64UrlFromBytes(new TextEncoder().encode(value))
}

async function signHs256(input: string, secret: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  )
  const signature = await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(input))
  return base64UrlFromBytes(new Uint8Array(signature))
}

/**
 * Builds the HS256 JWT the advisors-core socket expects, byte-for-byte compatible
 * with the original `jwt-simple` encoding (header {typ,alg}, base64url segments).
 * Uses Web Crypto so it runs in the browser (jwt-simple requires Node's crypto).
 * Payload: { userId: firebaseId, appleToken?, expiresIn, token: btoa(recurlyBaseURL) }.
 * `userId` is always the Firebase uid; `appleToken` is added only for Apple sign-in.
 */
export async function buildSocketAuthToken(user: User): Promise<string> {
  const appleToken = getAppleToken(user)
  const header = base64UrlFromString(JSON.stringify({ typ: "JWT", alg: "HS256" }))
  const payload = base64UrlFromString(
    JSON.stringify({
      userId: user.uid,
      ...(appleToken ? { appleToken } : {}),
      expiresIn: "1h",
      token: btoa(env.VITE_ADVISORS_RECURLY_BASE_URL),
    }),
  )
  const signingInput = `${header}.${payload}`
  const signature = await signHs256(signingInput, env.VITE_ADVISORS_SOCKET_SECRET)
  return `${signingInput}.${signature}`
}
